import { isDesktopRuntime } from './db';
import { getPendingWrites, markWriteStatus, logSyncFailure } from './queue';
import { handlerRegistry } from './handlers';
import { WriteQueueRow } from './db';

let draining = false;

// A failed write auto-retries a few times with growing backoff before
// requiring a person to click "Reintentar" -- most failures at this layer
// are transient (a network blip, a momentary 5xx), not permanent data
// problems, so silently retrying a bounded number of times catches those
// without anyone having to notice and act. The appointment slot-conflict
// case still gets its own immediate inline retry inside
// handlers/appointment.ts before ever reaching here.
const MAX_AUTO_RETRIES = 3;
const BASE_BACKOFF_MS = 30_000; // 30s
const MAX_BACKOFF_MS = 5 * 60_000; // capped at the safety-net interval's cadence

function computeBackoffMs(attemptNumber: number): number {
  return Math.min(BASE_BACKOFF_MS * 2 ** (attemptNumber - 1), MAX_BACKOFF_MS);
}

export type SyncListener = () => void;
const listeners = new Set<SyncListener>();

/** Notified after every drainQueue() run (success or not) so UI (e.g. SyncStatusPanel) can refresh. */
export function onSyncActivity(listener: SyncListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

/**
 * Drains the outbox: pulls pending writes, replays each via its named
 * handler, and marks it done/pending-with-backoff/failed. Processes
 * strictly in insertion (FIFO) order, which is also dependency order since
 * callers enqueue a batch in the sequence it must be replayed (see
 * queue.ts).
 *
 * Only 'pending' items due for replay are picked up (see
 * listPendingWrites' next_retry_at filter) -- a row that fails gets up to
 * MAX_AUTO_RETRIES automatic retries with growing backoff, staying
 * 'pending' the whole time so this same loop naturally retries it once its
 * backoff window passes. Only after auto-retries are exhausted does it
 * become 'failed' and require a person to explicitly retry it
 * (SyncStatusPanel.tsx).
 */
export async function drainQueue(): Promise<void> {
  if (!isDesktopRuntime() || draining) {
    return;
  }
  draining = true;
  try {
    const pending = await getPendingWrites();
    for (const row of pending) {
      await replayOne(row);
    }
  } finally {
    draining = false;
    notifyListeners();
  }
}

async function replayOne(row: WriteQueueRow): Promise<void> {
  const handler = handlerRegistry[row.handler];
  if (!handler) {
    await markWriteStatus(row.client_uuid, 'failed', `No handler registered: ${row.handler}`);
    return;
  }

  await markWriteStatus(row.client_uuid, 'in-flight');
  try {
    await handler(row);
    await markWriteStatus(row.client_uuid, 'done');
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[offline sync] ${row.handler} failed for ${row.client_uuid}:`, error);

    // The 'in-flight' update above just incremented attempt_count in the
    // DB; row.attempt_count is the pre-increment value read before this
    // replay started, so +1 is the count including this attempt -- no
    // extra IPC round-trip needed to know it.
    const attemptNumber = row.attempt_count + 1;
    await logSyncFailure({ ...row, attempt_count: attemptNumber }, message);

    if (attemptNumber <= MAX_AUTO_RETRIES) {
      const nextRetryAt = new Date(Date.now() + computeBackoffMs(attemptNumber)).toISOString();
      await markWriteStatus(row.client_uuid, 'pending', message, nextRetryAt);
    } else {
      await markWriteStatus(row.client_uuid, 'failed', message);
    }
  }
}

/**
 * Resets a failed item back to pending (clearing any leftover backoff
 * timestamp) so the next drainQueue() picks it up immediately. Note
 * attempt_count is never reset -- if this manual retry fails too, it goes
 * straight back to 'failed' rather than getting a fresh set of automatic
 * retries, since attempt_count is already past MAX_AUTO_RETRIES by the
 * time an item reaches 'failed' at all.
 */
export async function retryWrite(clientUuid: string): Promise<void> {
  await markWriteStatus(clientUuid, 'pending', null);
  notifyListeners();
}
